import React from "react";
//#region redux
import { AppState } from "store";
import { useSelector } from "react-redux";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { DateConstants } from "utils/constants";
import { Loading } from "models/enums/loading";
//#endregion
//#region components
import { ChartContextProvider, GridR, ViewPanel, WidgetArea } from "components/common";
import { SomethingWentWrongLoadingTodos, TodosStateWidget, TodosWidget } from "components/todos";
import * as Widgets from "./widgets";
import { Divider, Grid, Skeleton } from "@mui/material";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";
import { ExpandableListItemSkeleton } from "components/common/skeletons";

const Dashboard: React.FC = () => {
  const hasReportPermission = useSelector((state: AppState) => state.auth.selectedCompany.reportsAvailable);
  // Set from Heart, and already ANDed with the reports permission server-side.
  const bankBalanceWidgetActivated = useSelector(
    (state: AppState) => state.auth.selectedCompany.bankBalanceWidgetActivated
  );

  const failedToLoadTodos = useSelector((store: AppState) => store.todos.todoSummaryLoadingState === Loading.Failure);
  const todosLoading = useSelector(
    (store: AppState) =>
      store.todos.todoLoadingState === Loading.NotLoaded ||
      store.todos.todoLoadingState === Loading.Loading ||
      store.todos.todoSummaryLoadingState === Loading.NotLoaded ||
      store.todos.todoSummaryLoadingState === Loading.Loading
  );
  return (
    <WidgetArea>
      <Grid item xs>
        <Grid container rowGap={3} alignContent="space-between" height="100%">
          {todosLoading ? (
            <Grid container rowGap={3}>
              <Grid item xs={12}>
                <Grid container className={cssExports.todosWidgetHeader}>
                  <Grid item container xs className={cssExports.title} direction={"row"}>
                    <Skeleton variant="text" width={70} height={28} className={cssExports.skeletonSurfaceBackground} />
                    <Skeleton
                      variant="text"
                      width={36}
                      height={28}
                      sx={{ marginLeft: 1 }}
                      className={cssExports.skeletonSurfaceBackground}
                    />
                  </Grid>
                  <Grid item>
                    <Skeleton
                      variant="rounded"
                      width={100}
                      height={28}
                      sx={{ borderRadius: "9999px" }}
                      className={cssExports.skeletonSurfaceBackground}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <ExpandableListItemSkeleton />
            </Grid>
          ) : failedToLoadTodos ? (
            <SomethingWentWrongLoadingTodos />
          ) : (
            <Widgets.TodoWidgetArea>
              <TodosWidget />
              <TodosStateWidget />
            </Widgets.TodoWidgetArea>
          )}

          <GridR item xs={12}>
            <Widgets.DashboardActions />
          </GridR>
          <Widgets.AccountingDoneContainer />
        </Grid>
      </Grid>
      <Grid className={cssExports.widgetSeperator}>
        <Divider orientation="vertical" />
      </Grid>
      <Widgets.TopRightWidgetArea>
        <Widgets.Support />
        <Widgets.ReferFriend />
        <Widgets.News />
        <Widgets.SupportTeamWidget />
      </Widgets.TopRightWidgetArea>

      {hasReportPermission ? (
        <>
          <ChartContextProvider>
            <Widgets.DashboardMainChartWrapper />
          </ChartContextProvider>
          <ViewPanel
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.CostReductionChartTitle)}
            appearance="sm"
            gridSize="sm"
          >
            <Widgets.CostReportDoughnutChart />
          </ViewPanel>
          <ViewPanel
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.OperatingProfit)}
            appearance="sm"
            gridSize="sm"
          >
            <Widgets.OperatingProfitBarChart />
          </ViewPanel>
          <ViewPanel
            title={`${LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Common.Sale
            )} ${LocalizationHelperInstance.getFormattedDate(new Date(), DateConstants.MonthYearLongConfig, true)}`}
            appearance="sm"
            gridSize="sm"
          >
            <Widgets.SalesNumberChart />
          </ViewPanel>
          <ViewPanel
            title={`${LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Common.Sale
            )} ${LocalizationHelperInstance.getFormattedDate(new Date(), DateConstants.MonthLongConfig, true)}`}
            appearance="sm"
            gridSize="sm"
          >
            <Widgets.SalesBarChart />
          </ViewPanel>
          <ViewPanel
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.AccumulatedOperatingProfit)}
            appearance="sm"
            gridSize="sm"
          >
            <Widgets.OperatingProfitAccumulatedLineChart />
          </ViewPanel>
          {bankBalanceWidgetActivated ? (
            <ViewPanel
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.BankBalance)}
              appearance="sm"
              gridSize="sm"
            >
              <Widgets.BankBalanceWidget />
            </ViewPanel>
          ) : (
            <></>
          )}
          {/* Enable when performance is good */}
          {/* <GridR item container xs sm md={6} lg={4}>
            <Widgets.TopComments />
          </GridR> */}
        </>
      ) : (
        <></>
      )}
    </WidgetArea>
  );
};

export default Dashboard;
